import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Typography,
  Chip,
  Box,
  Divider,
} from "@mui/material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import useGetPrograms from "../../Hooks/useGetPrograms";
import useGetLocations from "../../Hooks/useGetLocation";
import usePermissions from "../../Reducers/usePermissions";
import { ADMIN, ROOT } from "../../Static/static";
import { useGetAnnouncements } from "../../Hooks/useGetAnnouncements";

// Validation Schema using Yup
const validationSchema = Yup.object({
  id: Yup.number().nullable(),
  title: Yup.string()
    .required("Title is required")
    .max(255, "Title must be 255 characters or less"),
  body: Yup.string()
    .required("Body is required")
    .max(1000, "Body must be 1000 characters or less"),
  location_id: Yup.number()
    .nullable(), // Allow an array of numbers or null
  severity: Yup.string()
    .required("Severity is required")
    .oneOf(["info", "warning", "error"], "Invalid severity level"),
  organization_id: Yup.number().required("Organization ID is required"),
  program_id: Yup.number().nullable(),
  admin_id: Yup.number().nullable(),
  staff_id: Yup.number().nullable()
});

const AddAnnouncement = ({ open, onClose, initialData }) => {
  const role = useSelector((state) => state.role);
  const { programs } = useGetPrograms(role);
  const { locations } = useGetLocations(role);
  const { createAnnouncement, updateAnnouncement } = useGetAnnouncements();
  const  { hasPermission } = usePermissions();

  const initialValues = {
    id: initialData?.id || null,
    title: initialData?.title || "",
    body: initialData?.body || "",
    location_id: initialData?.location_id|| null, 
    severity: initialData?.severity || "info",
    organization_id: role.organization_id,
    program_id: initialData?.program_id || null,
    admin_id: role.role === ROOT ? role.id: null,
    staff_id: role.role === ADMIN ? role.id : null
  };

  const handleSubmit = async (values) => {
    if (values.id != null) {
      const updated = await updateAnnouncement(values);
      if (updated) { onClose(); }
    }
    else {
      const created = await createAnnouncement(values);
      if (created) { onClose(); }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>
        <Typography variant="h6" fontWeight={'bold'}>
          {initialData ? "Edit Announcement" : "Add Announcement"}
        </Typography>
        <Typography variant="body2">
          Add/update a new announcement below
        </Typography>
      </DialogTitle>
      <Divider />
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ values, errors, touched, handleChange, handleBlur }) => (
          <Form>
            {console.log(errors)}
            <DialogContent>
              <TextField
                fullWidth
                margin="dense"
                label="Title"
                name="title"
                value={values.title}
                onChange={handleChange}
                error={touched.title && Boolean(errors.title)}
                helperText={touched.title && errors.title}
              />
              <TextField
                fullWidth
                margin="dense"
                label="Body"
                name="body"
                value={values.body}
                onChange={handleChange}
                error={touched.body && Boolean(errors.body)}
                helperText={touched.body && errors.body}
                multiline
                rows={5}
              />
                {
                    values.id === null ?
                    (
                        <FormControl margin="dense" fullWidth>
                            <InputLabel id="location-link">Link to location</InputLabel>
                            <Select
                            labelId="location-link"
                            id="location_id"
                            name="location_id"
                            label="Location link"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.location_id}
                            >
                            <MenuItem key={"NA"} value={null}>
                                {"NA"}
                            </MenuItem>
                            {locations && locations.map((location) => (
                                <MenuItem key={location.id} value={location.id}>
                                {location.name}
                                </MenuItem>
                            ))}
                            </Select>
                        </FormControl>
                    ):
                    <FormControl margin="dense" fullWidth>
                        <InputLabel id="location-link">Link to location</InputLabel>
                        <Select
                        labelId="location-link"
                        id="location_id"
                        name="location_id"
                        label="Location link"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.location_id}
                        disabled
                        >
                        <MenuItem key={"NA"} value={null}>
                            {"NA"}
                        </MenuItem>
                        {locations.map((location, index) => (
                            <MenuItem key={index} value={location.id}>
                            {location.name}
                            </MenuItem>
                        ))}
                        </Select>
                    </FormControl>
              }
              
              <FormControl fullWidth margin="dense">
                <InputLabel id="Severity-id">Severity</InputLabel>
                <Select
                  labelId="Severity-id"
                  name="severity"
                  value={values.severity}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.severity && Boolean(errors.severity)}
                > 
                
                  <MenuItem value="info">Info</MenuItem>
                  <MenuItem value="warning">Warning</MenuItem>
                  <MenuItem value="error">Urgent</MenuItem>
                </Select>
                {touched.severity && errors.severity && (
                  <Typography variant="caption" color="error">
                    {errors.severity}
                  </Typography>
                )}
              </FormControl>
              <FormControl fullWidth>
                <InputLabel id="programs-label">Programs</InputLabel>
                <Select
                  labelId="programs-label"
                  id="programs"
                  label="Programs"
                  name="program_id"
                  value={values.program_id}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.program_id && Boolean(errors.program_id)}
                >
                  <MenuItem key={"null"} value={null}>
                    NA
                  </MenuItem>
                  {programs.map((items, index) => (
                    <MenuItem key={index} value={items.id}>
                      {items.program_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </DialogContent>
            <DialogActions>
              <Button color="error" onClick={onClose}>Cancel</Button>
              <Button disabled={!hasPermission('write', 'announcements')} type="submit" variant="contained">
                Submit
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default AddAnnouncement;